import { CalendarBlankIcon } from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useEffect, useState } from "react";

type Props = {
  value: string;
  invalid?: boolean;
  onChange: (value: string) => void;
  onValidityChange: (valid: boolean) => void;
};

const CURRENT_SUFFIX = "Aujourd’hui";

const parsePeriod = (value: string) => {
  const [start = "", rawEnd = ""] = value.split(" – ");
  return {
    start,
    end: rawEnd === CURRENT_SUFFIX ? "" : rawEnd,
    current: rawEnd === CURRENT_SUFFIX,
  };
};

const serializePeriod = (start: string, end: string, current: boolean) => {
  if (!start) return "";
  if (current) return `${start} – ${CURRENT_SUFFIX}`;
  return end ? `${start} – ${end}` : start;
};

export const PeriodField = ({ value, invalid, onChange, onValidityChange }: Props) => {
  const initial = parsePeriod(value);
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [current, setCurrent] = useState(initial.current);
  const chronologyError = Boolean(start && end && !current && end < start);

  useEffect(() => {
    const next = parsePeriod(value);
    setStart(next.start);
    setEnd(next.end);
    setCurrent(next.current);
  }, [value]);

  useEffect(() => onValidityChange(!chronologyError), [chronologyError, onValidityChange]);

  const update = (nextStart: string, nextEnd: string, nextCurrent: boolean) => {
    setStart(nextStart);
    setEnd(nextEnd);
    setCurrent(nextCurrent);
    onChange(serializePeriod(nextStart, nextEnd, nextCurrent));
  };

  const inputClass = cn(
    "mt-1.5 h-11 w-full rounded-xl border bg-white px-3 text-xs font-medium text-[#071451] outline-none transition",
    "focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100",
    invalid || chronologyError ? "border-rose-400" : "border-slate-200",
  );

  return (
    <div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-[10px] font-semibold text-slate-500">
          Date de début
          <span className="relative block">
            <CalendarBlankIcon
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 z-10 mt-0.5 -translate-y-1/2 text-indigo-500"
            />
            <input
              type="month"
              value={start}
              max={current ? undefined : end || undefined}
              onChange={(event) => update(event.target.value, end, current)}
              className={cn(inputClass, "pl-9")}
            />
          </span>
        </label>
        <label className="block text-[10px] font-semibold text-slate-500">
          Date de fin
          <span className="relative block">
            <CalendarBlankIcon
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 z-10 mt-0.5 -translate-y-1/2 text-indigo-500"
            />
            <input
              type="month"
              value={end}
              min={start || undefined}
              disabled={current}
              onChange={(event) => update(start, event.target.value, false)}
              className={cn(
                inputClass,
                "pl-9 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400",
              )}
            />
          </span>
        </label>
      </div>
      <label className="mt-2.5 flex w-fit cursor-pointer items-center gap-2 text-[10px] font-semibold text-[#071451]">
        <input
          type="checkbox"
          checked={current}
          onChange={(event) => update(start, event.target.checked ? "" : end, event.target.checked)}
          className="size-4 rounded border-slate-300 text-emerald-500 focus:ring-emerald-400"
        />
        Jusqu’à aujourd’hui
      </label>
      {chronologyError && (
        <p className="mt-1 text-[9px] font-medium text-rose-600">
          La date de fin doit être postérieure ou égale à la date de début.
        </p>
      )}
    </div>
  );
};
